import React, { FunctionComponent, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { getDay, parseISO } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { sortBy, uniq } from 'lodash-es'
import styled from 'styled-components/native'

import {
  CALENDAR_SUPPORT_TICKET_LINK_DA,
  CALENDAR_SUPPORT_TICKET_LINK_OUS,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/constants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { ProviderBookableCalendarSettings } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/providerBookableCalendarSettings/ProviderBookableCalendarSettings'
import { ProviderHybridCalendarSettings } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/providerHybridCalendarSettings/ProviderHybridCalendarSettings'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'

import './providerBookableCalendar.scss'
import { VideoSessionPreferenceSettings } from './VideoSessionPreferenceSettings'
import { actions as mixpanelActions, mixpanelEvents } from '../../../mixpanel/mixpanelConstants'
import { track } from '../../../mixpanel/mixpanelTracking'
import CalendarSetupCard from '../calendar/CalendarSetupCard'
import { useGetCalendarAvailabilitySlotsQuery, useGetCalendarProviderQuery } from '../calendar/data/calendarApi'
import { useCalendarRequiresAuth } from '../calendar/data/hooks/useCalendarRequiresAuth'
import { useProviderCalendarLive } from '../calendar/hooks/useProviderCalendarLive'
import { useTimeZone } from '../calendar/hooks/useTimeZone'
import { getBookableTarget } from '../calendar/utils'
import { LC_CALENDAR_EDIT, LC_CALENDAR_EDIT_SELECT_CALENDARS } from '../common/constants/routingConstants'
import {
  getAuthConfig,
  getAuthEmploymentStatus,
  getAuthUserId,
  getAuthVideoPreference,
} from '../data/auth/authSelectors'

const Container = styled.View({
  width: '100%',
})

const Header = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  padding: theme.spacing['24px'],
  borderBottomWidth: '1px',
  borderBottomColor: theme.colors.borderDefault,
}))

const ContentContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  paddingTop: theme.spacing['24px'],
  gap: theme.spacing['32px'],
}))

const CalendarContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  maxWidth: '944px',
  width: '100%',
  gap: theme.spacing['16px'],
}))

const CalendarSettingsTitle = styled(Title)({
  alignSelf: 'flex-start',
})

const Settings: FunctionComponent = ({}) => {
  const { isInProductCalendarEnabled, isDAEngageOnOffPlatformVideoEnabled } = useFlags()
  const employmentStatus = useSelector(getAuthEmploymentStatus)
  const userId = useSelector(getAuthUserId)
  const config = useSelector(getAuthConfig)
  const videoPreference = useSelector(getAuthVideoPreference) ?? true
  const intl = useIntl()
  const timeZone = useTimeZone()
  const navigate = useNavigate()

  const { data: calendarProvider, isLoading: isCalendarProviderLoading } = useGetCalendarProviderQuery(
    {
      providerId: userId,
    },
    { refetchOnMountOrArgChange: true },
  )
  const { data: availabilitySlots, isLoading: isAvailabilitySlotsLoading } = useGetCalendarAvailabilitySlotsQuery(
    {
      providerId: userId,
    },
    { refetchOnMountOrArgChange: true },
  )
  const requiresAuth = useCalendarRequiresAuth()

  const providerCalendarLive = useProviderCalendarLive(config, calendarProvider)

  const initialEvents = useMemo(() => {
    return availabilitySlots?.map((slot) => ({
      startTime: slot.start_datetimetz,
      endTime: slot.end_datetimetz,
    }))
  }, [availabilitySlots])

  const availabilityText = useMemo(() => {
    const sortedByDay = sortBy(initialEvents, (event) => {
      const dateInZone = utcToZonedTime(event.startTime, timeZone)
      return getDay(dateInZone)
    })
    return intl.formatList(
      uniq(
        sortedByDay.map((event) => {
          const date = parseISO(event.startTime)
          return intl.formatDate(date, { weekday: 'long', timeZone })
        }),
      ),
    )
  }, [initialEvents, intl, timeZone])

  const target =
    (employmentStatus
      ? config?.inProductCalendar?.bookableTarget?.[employmentStatus]
      : getBookableTarget(employmentStatus)) ?? getBookableTarget(employmentStatus)
  if (!isInProductCalendarEnabled) {
    return null
  }

  if (initialEvents == null || isCalendarProviderLoading || isAvailabilitySlotsLoading) {
    return <LoadingIndicator />
  }

  return (
    <Container>
      <Header>
        <Title
          size={TitleSize.LARGE}
          text={<FormattedMessage defaultMessage='Settings' description='Header for settings' />}
        />
      </Header>
      <ContentContainer>
        <CalendarContainer>
          <CalendarSettingsTitle
            size={TitleSize.MEDIUM}
            text={<FormattedMessage defaultMessage='Lyra Calendar' description='Header for Lyra Calendar settings' />}
          />
          {requiresAuth || !providerCalendarLive ? (
            <CalendarSetupCard />
          ) : (
            <div className='lc-bookable-calendar'>
              {config?.inProductCalendar?.hybridBookingMode ? (
                <ProviderHybridCalendarSettings
                  availabilityText={availabilityText}
                  onAvailabilityEditPressed={() => {
                    // TODO: Add in Mixpanel event: DACN-1336
                    navigate(LC_CALENDAR_EDIT.route)
                  }}
                  subCalendarSelection={config?.inProductCalendar?.subCalendarSelection}
                  onSubCalendarEditPressed={() => {
                    // TODO: Add in Mixpanel event: DACN-1336
                    navigate(LC_CALENDAR_EDIT_SELECT_CALENDARS.route)
                  }}
                  onSubmitRequestPressed={() => {
                    // TODO: Add in Mixpanel event: DACN-1336
                    window.open(
                      config?.inProductCalendar?.useInternationalLinks
                        ? CALENDAR_SUPPORT_TICKET_LINK_OUS
                        : CALENDAR_SUPPORT_TICKET_LINK_DA,
                      '_blank',
                    )
                  }}
                />
              ) : (
                <ProviderBookableCalendarSettings
                  availabilityText={availabilityText}
                  events={initialEvents}
                  onEditPressed={() => {
                    track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.CALENDAR_TEMPLATE_EDIT })
                    navigate(LC_CALENDAR_EDIT.route)
                  }}
                  bookableTarget={target}
                  calendarProvider={calendarProvider}
                  hideBookableTarget={config?.inProductCalendar?.hideBookableTarget}
                />
              )}
            </div>
          )}
        </CalendarContainer>
        {isDAEngageOnOffPlatformVideoEnabled && config?.supportsOnOffPlatformVideo && videoPreference && (
          <CalendarContainer>
            <CalendarSettingsTitle
              size={TitleSize.MEDIUM}
              text={
                <FormattedMessage
                  defaultMessage='Video session preference'
                  description='Header for video session preference settings section'
                />
              }
            />
            <VideoSessionPreferenceSettings />
          </CalendarContainer>
        )}
      </ContentContainer>
    </Container>
  )
}

export default Settings
